import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloseIcon,
} from "@chakra-ui/icons";
import {
  Box,
  Container,
  Flex,
  Heading,
  Image,
  List,
  ListItem,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useEffect } from "react";
import { FaFacebookF, FaTwitter } from "react-icons/fa";
import { Link } from "react-router-dom";
import { trackEvent } from "../../../shared/analytics";
import usePageMeta from "../../../shared/hooks/usePageMeta";
import BookClubPromo, {
  BookClubAnnouncement,
} from "../components/BookClubPromo";
import LatestReadingOrders from "../components/LatestReadingOrders";
import "../styles/style.css";

const Homepage = () => {
  usePageMeta(
    "Redwing - Comic Book Reading Orders for Marvel, DC & Star Wars",
    "500+ curated comic reading orders for Marvel, DC, Star Wars & Image Comics. Track your reading progress across devices, free. No more spreadsheets.",
    "/"
  );

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "../scripts/main.min.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <div className="body-wrap boxed-container">
      <BookClubAnnouncement />

      <header className="site-header text-light">
        <Container maxW="container.xl">
          <div className="site-header-inner">
            <div className="brand header-brand text-center">
              <Image
                className="header-logo-image"
                src="/images/logo-512.png"
                alt="Redwing"
              />
            </div>
          </div>
        </Container>
      </header>

      <main>
        <section className="hero text-center text-light">
          <div className="hero-bg"></div>
          <div className="hero-particles-container">
            <canvas id="hero-particles"></canvas>
          </div>
          <div className="container-sm">
            <div className="hero-inner">
              <div className="hero-copy">
                <Heading
                  as="h1"
                  fontWeight="bolder"
                  size="2xl"
                  className="hero-title"
                  mb={3}
                >
                  Find your way into comics.
                </Heading>
                <Text mb={4} fontSize="xl" className="hero-paragraph">
                  Explore comics through clear reading paths and keep track of
                  your progress as you go.
                </Text>
                <p>
                  <Link
                    to="/signup"
                    id="signup"
                    className="button button-primary button-wide-mobile"
                    target="_parent"
                    onClick={() =>
                      trackEvent("signup_cta_click", { placement: "hero" })
                    }
                  >
                    GET STARTED FREE
                  </Link>
                  <Link
                    id="login"
                    className="button button-secondary button-wide-mobile"
                    to="/login"
                    target="_parent"
                  >
                    LOGIN
                  </Link>
                </p>
                <Text fontSize="sm" mt={2} mb={8} color="#CCCCCC">
                  Free forever plan · No credit card required
                </Text>
                <div className="plans-cta">
                  <p>
                    <a
                      className="button-wide-mobile"
                      href="https://play.google.com/store/apps/details?id=app.theredwing"
                      onClick={() =>
                        trackEvent("app_download_click", {
                          platform: "google_play",
                          placement: "hero",
                        })
                      }
                    >
                      <Image
                        src="/images/download-android.png"
                        width="150"
                        alt="Get Redwing on Google Play"
                      />
                    </a>
                    <a
                      className="button-wide-mobile"
                      href="https://apps.apple.com/us/app/redwing-app/id1604419147"
                      onClick={() =>
                        trackEvent("app_download_click", {
                          platform: "app_store",
                          placement: "hero",
                        })
                      }
                    >
                      <Image
                        src="/images/download-ios.png"
                        width="150"
                        alt="Download Redwing on the App Store"
                      />
                    </a>
                  </p>
                </div>
              </div>
              <div className="mockup-container">
                <div className="mockup-bg">
                  <Image
                    src="/images/iphone-hero-bg.svg"
                    alt="iPhone illustration"
                  />
                </div>
                <Image
                  className="device-mockup"
                  src="/images/app-preview.png"
                  alt="iPhone Hero"
                />
              </div>
            </div>
          </div>
        </section>

        <BookClubPromo />

        {/* Latest reading orders */}
        <LatestReadingOrders />

        <section className="features-extended section">
          <div className="features-extended-inner section-inner">
            <div className="features-extended-wrap">
              <Container maxW="container.xl">
                <div className="feature-extended">
                  <div className="feature-extended-image">
                    <div className="mockup-bg">
                      <Image
                        src="/images/iphone-feature-bg-01.svg"
                        alt="Reading orders"
                      />
                    </div>
                    <Image
                      className="device-mockup is-revealing"
                      src="/images/browse.png"
                      alt="Browsing Marvel comic reading orders in the Redwing app"
                      loading="lazy"
                    />
                  </div>
                  <div className="feature-extended-body is-revealing">
                    <Heading as="h3" fontSize="4xl" mb={6}>
                      500+ reading orders
                    </Heading>
                    <Text fontSize="2xl">
                      Read the way you want: by character, team, storyline,
                      creator, or the entire universe.
                    </Text>
                  </div>
                </div>
                <div className="feature-extended">
                  <div className="feature-extended-image">
                    <div className="mockup-bg">
                      <img
                        src="/images/iphone-feature-bg-02.svg"
                        alt="Track progress"
                      />
                    </div>
                    <img
                      className="device-mockup is-revealing"
                      src="/images/reading-progress.png"
                      alt="Tracking comic reading progress in the Redwing app"
                      loading="lazy"
                    />
                  </div>
                  <div className="feature-extended-body is-revealing">
                    <Heading as="h3" fontSize="4xl" mb={6}>
                      No more spreadsheets
                    </Heading>
                    <Text fontSize="2xl">
                      Keep your reading organized without the hassle. Your
                      progress syncs automatically across multiple reading
                      orders, with quick links to your favorite comics apps.
                    </Text>
                    <Text
                      mt={5}
                      color="#CCCCCC"
                      fontSize="sm"
                      letterSpacing="1px"
                    >
                      DIRECT LINKS WORKING WITH:
                    </Text>
                    <Flex gap={2} my={3} pl={{ base: 2, md: 0 }}>
                      <Image
                        src="/images/mu.png"
                        maxH="60px"
                        alt="Marvel Unlimited"
                        loading="lazy"
                      />
                      <Image
                        src="/images/dcui-app.png"
                        maxH="60px"
                        alt="DC Universe Infinite"
                        style={{ marginLeft: "8px" }}
                        loading="lazy"
                      />
                    </Flex>
                    <Text fontSize="md" mt={4}>
                      Note: Redwing works alongside the apps below. It does not
                      provide the comics themselves.
                    </Text>
                  </div>
                </div>
                <div className="feature-extended">
                  <div className="feature-extended-image">
                    <div className="mockup-bg">
                      <Image
                        src="/images/iphone-feature-bg-03.svg"
                        alt="Comics News"
                      />
                    </div>
                    <Image
                      className="device-mockup is-revealing"
                      src="/images/news.png"
                      alt="Latest Marvel and MCU news in the Redwing app"
                      loading="lazy"
                    />
                  </div>
                  <div className="feature-extended-body is-revealing">
                    <Heading as="h3" fontSize="4xl" mb={6}>
                      Stay updated on the latest releases
                    </Heading>
                    <Text fontSize="2xl">
                      Follow every new Marvel comic and MCU release. All the
                      latest news, reviews, and updates from top comic websites,
                      gathered in one convenient place.
                    </Text>
                  </div>
                </div>
                <Box mt={32} className="feature-extended">
                  <div className="feature-extended-image">
                    <div className="mockup-bg">
                      <Image
                        src="/images/iphone-feature-bg-03.svg"
                        alt="Redwing Desktop"
                      />
                    </div>
                    <Image
                      className="device-mockup is-revealing"
                      src="/images/redwing-desktop.png"
                      alt="Redwing comic reading order tracker on desktop"
                      style={{ maxWidth: 650 }}
                      loading="lazy"
                    />
                  </div>
                  <div className="feature-extended-body is-revealing">
                    <Heading as="h3" fontSize="4xl" mb={6}>
                      Now beyond mobile
                    </Heading>
                    <Text fontSize="2xl">
                      All the mobile features you love on a bigger screen,
                      synced flawlessly with the mobile app.
                    </Text>
                  </div>
                </Box>
              </Container>
            </div>
          </div>
        </section>

        <section className="features-extended section">
          <div className="features-extended-inner section-inner">
            <div className="features-extended-wrap">
              <div className="container">
                <div className="feature-extended">
                  <div id="pricing">
                    <Heading as="h2" fontSize="5xl" mb={6} textAlign="center">
                      Explore comics your way
                    </Heading>
                    <Text mt={4} mb={8} fontSize="xl" textAlign="center">
                      Unlock all the features for the cost of a cup of coffee
                      per month.{" "}
                      <Image
                        className="coffee"
                        src="/images/starbucks.png"
                        maxHeight="30px"
                        alt="(a cup of coffe at Starbucks)"
                      />
                    </Text>
                    <Flex
                      className="pricing-table-scroll-hint"
                      fontSize="sm"
                      color="gray.400"
                      align="center"
                      justify="center"
                      gap={1}
                    >
                      <ChevronLeftIcon aria-hidden="true" />
                      <Text as="span">
                        Swipe left or right to compare plans
                      </Text>
                      <ChevronRightIcon aria-hidden="true" />
                    </Flex>
                    <Box
                      className="pricing-table-scroll"
                      tabIndex={0}
                      aria-label="Pricing comparison table"
                    >
                      <Table variant="simple" size="md">
                        <Thead>
                          <Tr>
                            <Th px={6} py={4}>
                              &nbsp;
                            </Th>
                            <Th px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <Text fontSize="md">FREE</Text>
                              </Box>
                            </Th>
                            <Th px={6} py={4}>
                              <Box
                                textAlign="center"
                                color="yellow.500"
                                fontSize="lg"
                              >
                                PREMIUM
                              </Box>
                            </Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          <Tr>
                            <Td px={6} py={4}>
                              <Text fontSize="lg" fontWeight="bold">
                                Marvel reading orders
                              </Text>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CheckIcon color="green.500" />
                              </Box>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CheckIcon color="green.500" />
                              </Box>
                            </Td>
                          </Tr>
                          <Tr>
                            <Td px={6} py={4}>
                              <Text fontSize="lg" fontWeight="bold">
                                DC reading orders
                              </Text>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CheckIcon color="green.500" />
                              </Box>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CheckIcon color="green.500" />
                              </Box>
                            </Td>
                          </Tr>
                          <Tr>
                            <Td px={6} py={4}>
                              <Text fontSize="lg" fontWeight="bold">
                                Star Wars reading orders
                              </Text>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CheckIcon color="green.500" />
                              </Box>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CheckIcon color="green.500" />
                              </Box>
                            </Td>
                          </Tr>
                          <Tr>
                            <Td px={6} py={4}>
                              <Text fontSize="lg" fontWeight="bold">
                                Image Comics reading orders
                              </Text>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CheckIcon color="green.500" />
                              </Box>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CheckIcon color="green.500" />
                              </Box>
                            </Td>
                          </Tr>
                          <Tr>
                            <Td px={6} py={4}>
                              <Text fontSize="lg" fontWeight="bold">
                                Direct links to comics apps
                              </Text>
                              <Text fontSize="md">
                                Marvel Unlimited and DC Universe Infinite
                              </Text>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CloseIcon color="red.500" />
                              </Box>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CheckIcon color="green.500" />
                              </Box>
                            </Td>
                          </Tr>
                          <Tr>
                            <Td px={6} py={4}>
                              <Text fontSize="lg" fontWeight="bold">
                                Unlimited tracking
                              </Text>
                              <Text fontSize="md">
                                Free tracks 50 issues per reading order. Premium
                                has no limit
                              </Text>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CloseIcon color="red.500" />
                              </Box>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CheckIcon color="green.500" />
                              </Box>
                            </Td>
                          </Tr>
                          <Tr>
                            <Td px={6} py={4}>
                              <Text fontSize="lg" fontWeight="bold">
                                Reading order requests
                              </Text>
                              <Text fontSize="md">
                                Ask for a reading order, get it within 28 days
                              </Text>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CloseIcon color="red.500" />
                              </Box>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CheckIcon color="green.500" />
                              </Box>
                            </Td>
                          </Tr>
                          <Tr>
                            <Td px={6} py={4}>
                              <Text fontSize="lg" fontWeight="bold">
                                Your reading orders library
                              </Text>
                              <Text fontSize="md">
                                Create your own orders and access them on mobile
                              </Text>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CloseIcon color="red.500" />
                              </Box>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CheckIcon color="green.500" />
                              </Box>
                            </Td>
                          </Tr>
                          <Tr>
                            <Td px={6} py={4}>
                              <Text fontSize="lg" fontWeight="bold">
                                Clone & Edit
                              </Text>
                              <Text fontSize="md">
                                Clone any existing reading order and make
                                changes to it.
                              </Text>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CloseIcon color="red.500" />
                              </Box>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CheckIcon color="green.500" />
                              </Box>
                            </Td>
                          </Tr>
                          <Tr>
                            <Td px={6} py={4}>
                              <Text fontSize="lg" fontWeight="bold">
                                Reading goals
                              </Text>
                              <Text fontSize="md">
                                Set a deadline, pace or yearly challenge and
                                track your progress toward it
                              </Text>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CloseIcon color="red.500" />
                              </Box>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CheckIcon color="green.500" />
                              </Box>
                            </Td>
                          </Tr>
                          <Tr>
                            <Td px={6} py={4}>
                              <Text fontSize="lg" fontWeight="bold">
                                Premium priority support
                              </Text>
                              <Text fontSize="md">
                                Live chat available for you to share any issues
                              </Text>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CloseIcon color="red.500" />
                              </Box>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CheckIcon color="green.500" />
                              </Box>
                            </Td>
                          </Tr>
                          <Tr>
                            <Td px={6} py={4}>
                              <Text fontSize="lg" fontWeight="bold">
                                Exclusive access to new features
                              </Text>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CloseIcon color="red.500" />
                              </Box>
                            </Td>
                            <Td px={6} py={4}>
                              <Box display="flex" justifyContent="center">
                                <CheckIcon color="green.500" />
                              </Box>
                            </Td>
                          </Tr>
                          <Tr>
                            <Td px={6} py={4}>
                              &nbsp;
                            </Td>
                            <Td
                              px={6}
                              py={4}
                              verticalAlign="top"
                              textAlign="center"
                            >
                              <Box justifyContent="center">
                                <Text fontSize="2xl" fontWeight="bold">
                                  $0.00/month
                                </Text>
                              </Box>
                            </Td>
                            <Td
                              px={6}
                              py={4}
                              verticalAlign="top"
                              textAlign="center"
                            >
                              <Box justifyContent="center">
                                <Text fontSize="2xl" fontWeight="bold" mb={3}>
                                  $2.99/month
                                </Text>
                                <Text fontSize="md" color="yellow.500">
                                  or $29.99/year (save 16%)
                                </Text>
                              </Box>
                            </Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </Box>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer" id="signup-cta">
        <div className="footer-particles-container">
          <canvas id="footer-particles"></canvas>
        </div>
        <div className="site-footer-top">
          <section className="cta section text-light">
            <div className="container-sm">
              <div className="cta-inner section-inner">
                <div className="cta-header text-center">
                  <Heading as="h2" className="section-title" mb={6}>
                    Join Redwing now
                  </Heading>
                  <div className="plans-cta">
                    <Box className="m-1">
                      <Link
                        id="signup-footer"
                        className="button button-primary button-wide-mobile footer-signup-button"
                        to="/signup"
                        onClick={() =>
                          trackEvent("signup_cta_click", {
                            placement: "footer",
                          })
                        }
                      >
                        Sign up for free
                      </Link>
                      <a
                        className="button-wide-mobile"
                        href="https://play.google.com/store/apps/details?id=app.theredwing"
                        onClick={() =>
                          trackEvent("app_download_click", {
                            platform: "google_play",
                            placement: "footer",
                          })
                        }
                      >
                        <Image
                          className="lg"
                          src="/images/download-android.png"
                          alt="Get Redwing on Google Play"
                        />
                      </a>
                      <a
                        className="button-wide-mobile"
                        href="https://apps.apple.com/us/app/redwing-app/id1604419147"
                        onClick={() =>
                          trackEvent("app_download_click", {
                            platform: "app_store",
                            placement: "footer",
                          })
                        }
                      >
                        <Image
                          className="lg"
                          src="/images/download-ios.png"
                          alt="Download Redwing on the App Store"
                        />
                      </a>
                      <Text className="footer-signup-copy" mt={6} fontSize="xl">
                        Sign up directly via our website, the app and App Store
                        or Google Play subscriptions.
                      </Text>
                    </Box>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
        <div className="site-footer-bottom">
          <Container maxW="container.xl">
            <div className="site-footer-inner">
              <div className="brand footer-brand">
                <Link to="/">
                  <Image src="/images/logo-512.png" maxH="64px" alt="Redwing" />
                </Link>
              </div>
              <List
                className="footer-links list-reset"
                display="flex"
                listStyleType="none"
              >
                <ListItem>
                  <Link to="/terms">Terms & conditions</Link>
                </ListItem>
                <ListItem>
                  <Link to="/privacy">Privacy policy</Link>
                </ListItem>
                <ListItem>
                  <Link to="/eula">End User License Agreement</Link>
                </ListItem>
                <ListItem>
                  <Link to="/cookie">Cookie policy</Link>
                </ListItem>
                <ListItem>
                  <a href="mailto:contact@redwing.app">Contact</a>
                </ListItem>
              </List>
              <ul className="footer-social-links list-reset">
                <li>
                  <a href="https://fb.me/theredwingapp">
                    <span className="screen-reader-text">Facebook</span>
                    <FaFacebookF size={24} color="#FFF" />
                  </a>
                </li>
                <li>
                  <a href="https://twitter.com/TheRedwingApp">
                    <span className="screen-reader-text">Twitter</span>
                    <FaTwitter size={24} color="#FFF" />
                  </a>
                </li>
              </ul>
              <div className="footer-copyright">
                &copy; 2023 Redwing, all rights reserved
              </div>
            </div>
          </Container>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;
